import { useEffect } from "react";
import * as React from "react";
import { Typography, Radio, Form, Input, Select, Row, Col, DatePicker } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';

import IconButton from "../../ui/IconButton";
import FormHeader from "../FormHeader";
import { DateValue, CurrencyValue, DateSelectionType, CurrencySelectionType,  TradedFundsPlaceholderName} from "../../../types/newLinkBuilder";
import useFetchCurrencies from "../../../utils/useFetchCurrencies";

interface DateAndCurrencyFormProps {
    dateValue: DateValue;
    setDateValue: (value: DateValue) => void;
    currencyValue: CurrencyValue;
    setCurrencyValue: (value: CurrencyValue) => void;
    handlePlaceholder: (name: TradedFundsPlaceholderName) => void;
    isVolumeAndTrendingCurrencySelected: boolean;
}

const DateAndCurrencyForm: React.FC<DateAndCurrencyFormProps> = ({ dateValue, setDateValue, currencyValue, setCurrencyValue, handlePlaceholder, isVolumeAndTrendingCurrencySelected }) => {
    const { currencyOptions, fetchCurrencies } = useFetchCurrencies();

    useEffect(() => {
        fetchCurrencies();
    }, []);

    const handleAsOfDateChange = (field: keyof DateValue, value: string) => {
        setDateValue({
            ...dateValue,
            [field]: value
        })
    }

    const handleCurrencyTypeChange = (field: keyof CurrencyValue, value: string) => {
        setCurrencyValue({
            ...currencyValue,
            [field]: value
        })
    }
    return (
        <div>
            <FormHeader title={`${isVolumeAndTrendingCurrencySelected ? "Date" : "Date and Currency"}`} />
            {!isVolumeAndTrendingCurrencySelected && (
                        <Typography.Title level={5} style={{ textAlign: 'left' }}>
                            <u>As-of-date</u>
                        </Typography.Title>
            )}
            <Form.Item
                name={"asOfDate"}
            >
                <Radio.Group
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                    }}
                    options={[{
                        value: DateSelectionType.Date,
                        label: (
                            <Row
                                align={"middle"}
                            >
                                <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                    <span>Date</span>
                                </Col>

                                <Col span={18} style={{ display: 'flex', justifyContent: 'start' }}>
                                    <DatePicker
                                        format="MM/DD/YYYY"
                                        placeholder="Select a date"
                                        maxDate={dayjs()}
                                        disabled={dateValue.selected === DateSelectionType.CellReference}
                                        style={{ width: "100%" }}
                                        value={dateValue.date ? dayjs(dateValue.date) : null}
                                        onChange={(value) => handleAsOfDateChange("date", value ? dayjs(value).toISOString() : "")}
                                        onClick={(e) => e.preventDefault()}
                                    />
                                </Col>
                            </Row>
                        )
                    }, {
                        value: DateSelectionType.CellReference,
                        label: (
                            <Row
                                align={"middle"}
                            >
                                <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                    <span>Cell Reference</span>
                                </Col>

                                <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                    <Input
                                        placeholder="Cell Reference"
                                        disabled={dateValue.selected === DateSelectionType.Date}
                                        value={dateValue.dateCellReference}
                                        onChange={(e) => handleAsOfDateChange("dateCellReference", e.target.value)}
                                        onClick={(e) => e.preventDefault()}
                                    />
                                </Col>

                                <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                    <IconButton
                                        disabled={dateValue.selected === DateSelectionType.Date}
                                        icon={<UploadOutlined />}
                                        onClick={() => handlePlaceholder(TradedFundsPlaceholderName.DateCellReference)}
                                    />
                                </Col>
                            </Row>
                        )
                    }
                    ]}
                />
            </Form.Item>

            {!isVolumeAndTrendingCurrencySelected && (
                <>
                    <Typography.Title level={5} style={{ textAlign: 'left' }}>
                        <u>Currency Type</u>
                    </Typography.Title>
                    <Form.Item
                        name={"currencyType"}
                    >
                        <Radio.Group
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                            }}
                            options={[{
                                value: CurrencySelectionType.Value,
                                label: (
                                    <Row
                                        align={"middle"}
                                    >
                                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <span>Value</span>
                                        </Col>
                                        <Col span={18} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <Select
                                                placeholder="Currency"
                                                disabled={currencyValue.selected === CurrencySelectionType.CellReference}
                                                value={currencyValue.currency}
                                                onChange={(value) => handleCurrencyTypeChange("currency", value)}
                                                style={{ width: "100%" }}
                                                onClick={(e) => e.preventDefault()}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={currencyOptions}
                                            />
                                        </Col>
                                    </Row>
                                )
                            }, {
                                value: CurrencySelectionType.CellReference,
                                label: (
                                    <Row
                                        align={"middle"}
                                    >
                                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <span>Cell Reference</span>
                                        </Col>
                                        <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <Input
                                                placeholder="Currency Type"
                                                disabled={currencyValue.selected === CurrencySelectionType.Value}
                                                value={currencyValue.currencyCellReference}
                                                onChange={(e) => handleCurrencyTypeChange("currencyCellReference", e.target.value)}
                                            />
                                        </Col>
                                        <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                            <IconButton
                                                disabled={currencyValue.selected === CurrencySelectionType.Value}
                                                icon={<UploadOutlined />}
                                                onClick={() => handlePlaceholder(TradedFundsPlaceholderName.CurrencyCellReference)}
                                            />
                                        </Col>
                                    </Row>
                                )
                            }
                            ]}
                        />
                    </Form.Item>
                </>
            )}
        </div>
    )
}

export default DateAndCurrencyForm; 